import React from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { CrudPage } from "../components/CrudPage";
import { MessageForm } from "../components/forms/MessageForm";
import { Pagination } from "../components/Pagination";
import { messages } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { getMessageCta, getMessageImage } from "../utils/messages";

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString("fa-IR") : "—";

const columns = [
  { header: "عنوان", accessor: "title" },
  {
    header: "مخاطب",
    accessor: "audienceType",
    render: (item: any) =>
      item.audienceType === "roles"
        ? item.targetRoles?.join("، ") || "—"
        : "همه کاربران مجاز",
  },
  {
    header: "فعال",
    accessor: "isActive",
    render: (item: any) => (item.isActive ? "✓" : "—"),
  },
  {
    header: "صفحه اصلی",
    accessor: "showInHome",
    render: (item: any) => (item.showInHome ? "✓" : "—"),
  },
  {
    header: "ایجادکننده",
    accessor: "createdBy",
    render: (item: any) =>
      item.createdBy?.fullName || item.createdBy?.username || "—",
  },
  {
    header: "ایجاد",
    accessor: "createdAt",
    render: (item: any) => formatDate(item.createdAt),
  },
];

const MessageInbox = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMessage, setSelectedMessage] = React.useState<any>(null);
  const [page, setPage] = React.useState(1);
  const queryClient = useQueryClient();
  const cardBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const unreadBg = useColorModeValue("brand.50", "badge.dark.bg");
  const pageSize = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["messages", "inbox", page],
    queryFn: () => messages.getAll(page, pageSize),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => messages.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
  });

  const inboxItems = data?.messages || [];
  const unreadCount = data?.unreadCount || 0;
  const selectedMessageImage = selectedMessage
    ? getMessageImage(selectedMessage)
    : "";
  const selectedMessageCta = getMessageCta(selectedMessage);

  const handleOpenMessage = async (item: any) => {
    setSelectedMessage(item);
    onOpen();

    if (!item.isRead) {
      await markReadMutation.mutateAsync(item._id);
      setSelectedMessage((prev: any) =>
        prev
          ? { ...prev, isRead: true, readAt: new Date().toISOString() }
          : prev,
      );
    }
  };

  return (
    <Box h="100%" overflow="auto" p={{ base: 4, md: 6 }}>
      <Container maxW="container.xl">
        <VStack align="stretch" spacing={6}>
          <HStack justify="space-between" flexWrap="wrap">
            <Box>
              <Heading size="lg" color={textColor}>
                پیام‌ها
              </Heading>
              <Text mt={2} color={subtitleColor}>
                پیام‌های داخلی و اطلاع‌رسانی‌های مرتبط با نقش شما در این بخش
                نمایش داده می‌شود.
              </Text>
            </Box>
            <Badge
              colorScheme={(unreadCount || 0) > 0 ? "brand" : "gray"}
              px={3}
              py={2}
              borderRadius="full"
            >
              {(unreadCount || 0) > 0
                ? `${unreadCount.toLocaleString("fa-IR")} پیام جدید`
                : "همه پیام‌ها خوانده شده‌اند"}
            </Badge>
          </HStack>

          {isLoading ? (
            <HStack justify="center" py={16}>
              <Spinner size="lg" />
            </HStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              {inboxItems.map((item: any) => (
                <Box
                  key={item._id}
                  bg={item.isRead ? cardBg : unreadBg}
                  borderRadius="2xl"
                  border="1.5px solid"
                  borderColor={borderColor}
                  p={5}
                  cursor="pointer"
                  onClick={() => handleOpenMessage(item)}
                  _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                  transition="all 0.2s ease"
                >
                  <VStack align="stretch" spacing={3}>
                    <HStack
                      justify="space-between"
                      align="start"
                      flexWrap="wrap"
                    >
                      <Heading size="md" color={textColor}>
                        {item.title}
                      </Heading>
                      <Badge colorScheme={item.isRead ? "gray" : "brand"}>
                        {item.isRead ? "خوانده شده" : "جدید"}
                      </Badge>
                    </HStack>
                    <Text color={subtitleColor} noOfLines={2} lineHeight="1.9">
                      {item.body}
                    </Text>
                    <HStack justify="space-between" flexWrap="wrap">
                      <Text fontSize="sm" color={subtitleColor}>
                        {formatDate(item.createdAt)}
                      </Text>
                      <Text fontSize="sm" color={subtitleColor}>
                        {item.readAt
                          ? `آخرین مطالعه: ${formatDate(item.readAt)}`
                          : "هنوز مطالعه نشده"}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              ))}
              {inboxItems.length === 0 ? (
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={borderColor}
                  p={8}
                  textAlign="center"
                >
                  <Text color={subtitleColor}>
                    در حال حاضر پیامی برای نمایش وجود ندارد.
                  </Text>
                </Box>
              ) : null}
            </VStack>
          )}

          {!isLoading && (data?.totalPage || 0) > 1 ? (
            <Pagination
              currentPage={data?.currentPage || 1}
              totalPages={data?.totalPage || 1}
              onPageChange={setPage}
            />
          ) : null}
        </VStack>
      </Container>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader>{selectedMessage?.title}</ModalHeader>
          <ModalCloseButton left={4} right="auto" />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" flexWrap="wrap">
                <Badge colorScheme={selectedMessage?.isRead ? "gray" : "brand"}>
                  {selectedMessage?.isRead ? "خوانده شده" : "جدید"}
                </Badge>
                <Text fontSize="sm" color={subtitleColor}>
                  {formatDate(selectedMessage?.createdAt)}
                </Text>
              </HStack>
              {selectedMessageImage ? (
                <Image
                  src={selectedMessageImage}
                  alt={selectedMessage?.title || "message image"}
                  w="100%"
                  objectFit="cover"
                  borderRadius="xl"
                />
              ) : null}
              <Text color={textColor} lineHeight="2.1" whiteSpace="pre-wrap">
                {selectedMessage?.body}
              </Text>
              {selectedMessageCta ? (
                selectedMessageCta.type === "external" ? (
                  <Button
                    as="a"
                    href={selectedMessageCta.url}
                    target={selectedMessageCta.openInNewTab ? "_blank" : undefined}
                    rel={
                      selectedMessageCta.openInNewTab
                        ? "noopener noreferrer"
                        : undefined
                    }
                    onClick={onClose}
                    w="full"
                    colorScheme="brand"
                  >
                    {selectedMessageCta.label}
                  </Button>
                ) : (
                  <Button
                    as={RouterLink}
                    to={selectedMessageCta.url}
                    onClick={onClose}
                    w="full"
                    colorScheme="brand"
                  >
                    {selectedMessageCta.label}
                  </Button>
                )
              ) : null}
              <HStack justify="space-between" pt={2}>
                <Text fontSize="sm" color={subtitleColor}>
                  {selectedMessage?.readAt
                    ? `خوانده شده در ${formatDate(selectedMessage.readAt)}`
                    : "هنوز خوانده نشده"}
                </Text>
                <Button variant="ghost" onClick={onClose}>
                  بستن
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export const MessagesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManageMessages =
    hasPermission("messages:create") ||
    hasPermission("messages:update") ||
    hasPermission("messages:delete");

  if (!canManageMessages) {
    return <MessageInbox />;
  }

  return (
    <CrudPage
      title="پیام‌ها"
      columns={columns}
      queryKey="messages"
      resourceName="messages"
      api={messages}
      FormComponent={MessageForm}
    />
  );
};
